import moment from 'moment';
import { projectFinal } from './projection';

/**
 * 調漲的住房率上界：悲觀預測 (P25) 要超過它才建議調漲，與目標住房率無關。
 *
 * 目標住房率回答的是「業績要賣到哪裡」，調漲要回答的是「需求是不是多到房間裝不下」。
 * 調漲本身會讓住房率往下掉，所以悲觀預測只高於目標還不夠 —— 超出上界的部分
 * 才是漲價後可以讓掉、仍能維持在上界以上的緩衝。
 *
 * 取 95% 而非 100%：2026-10-14 實測未來 90 天，100% 只挑得出 1 天，
 * P25 99.1% 的日子會因 0.9 個百分點被排除；95% 挑出 3 天，三天的中位預測都 ≥ 97%。
 */
export const RAISE_CEILING = 95;
/** 調漲的提前天數下限：太近才漲來不及發酵。無上限 —— 提前愈久效益期愈長。 */
export const RAISE_MIN_LEAD = 7;
/** 調漲的剩餘可售下限：只剩幾間時漲價影響的房量太少，不值得動作。 */
export const RAISE_MIN_ROOMS = 5;
/** 卡片載入的天數 (明天起算)。調漲清單看提前 RAISE_MIN_LEAD 天到這裡，調降另有 LOWER_MAX_LEAD 上限。 */
export const HORIZON_DAYS = 90;
/**
 * 調降的提前天數上限。散客訂房多集中在入住前一個月內，更早的低在手多屬正常提前節奏，
 * 此時降價常是白降。實測不設此門檻時調降會有 56 天，多數是「九月平日歷來就弱」的季節性。
 * 原為 21 天 (經驗法則，未經資料驗證)；2026-10-14 依營運判斷放寬為 30 天，讓提早落後的日子也能及早處理。
 */
export const LOWER_MAX_LEAD = 30;
/**
 * 只在五年的離散度都無法動搖結論時才開口 —— 這是「客觀」的可操作定義。
 * 帶跨過門檻就閉嘴，不給點估計再假裝它準。
 *
 * 調漲與調降各自一個判斷式而非共用一個：兩者的門檻不同 (上界 vs 目標)，
 * 目標調到 100% 時同一個預測帶可能同時滿足兩邊，共用一個回傳值只能靠判斷順序硬選一邊。
 */
export function isRaise(band) {
  return !!band && band.lo > RAISE_CEILING;
}

export function isLower(band, target) {
  return !!band && band.hi < target;
}

/**
 * 降價 d 後不傷 RevPAR 所需的在手率：RevPAR = ADR × 在手，降 d 就得把在手填到 ÷(1−d)。
 * 填不到就是降價傷 RevPAR。
 */
export function breakEvenFill(occ, discount) {
  return Math.min(100, Math.round(occ / (1 - discount)));
}

const isoOf = (dat) => dat.replace(/\//g, '-');

/** 逐日算出預測帶與共用欄位；樣本不足者 band 為 null，後續一律排除。 */
function decorate(items, todayIso) {
  const today = moment(todayIso, 'YYYY-MM-DD');
  return items.map((it) => {
    const unsold = Math.max(0, (it.cap || 0) - (it.otb_rooms || 0));
    return {
      dat: it.dat,
      iso: isoOf(it.dat),
      lead: moment(it.dat, 'YYYY/MM/DD').diff(today, 'days'),
      band: projectFinal(it),
      occNow: it.occ_now,
      cap: it.cap,
      otbRooms: it.otb_rooms,
      // 團散分層可能缺席 (端點舊版或舊快取)：一律轉成 null 而不是 0 ——
      // 「沒有這個欄位」與「這天真的一團都沒有」是兩件事
      otbGroup: it.otb_group == null ? null : it.otb_group,
      otbFit: it.otb_fit == null ? null : it.otb_fit,
      adr: it.adr,
      netPickup30: it.net_pickup30,
      unsold,
      upside: Math.round(unsold * (it.adr || 0)),
    };
  });
}

/**
 * 兩張清單：建議調漲與建議調降。
 *
 * 建議最短住宿原本是第三張清單，已併入調漲：最短住宿和漲價一樣會擋掉一部分需求，
 * 旺日本身都不確定會滿時把單晚客人擋掉，最後可能旺日也賣不滿，所以入選標準應與調漲相同。
 * 調漲清單改以鄰日資訊提示「可考慮最短住宿」，要不要設由使用者判斷。
 *
 * 排序：調降依待售間夜 × ADR 降冪；調漲依滿房後擋掉的房租降冪 (相同時依超出上界)。
 *
 * @param items         pms/ops/pricing-actions 的 items
 * @param target        目標住房率 (百分點)，用於調降與調漲的最短住宿提示；調漲本身看 RAISE_CEILING
 * @param todayIso      'YYYY-MM-DD'
 * @param specialDates  Set of 'YYYY-MM-DD'，holidays.category === 'special'
 */
export function buildLists(items, { target, todayIso, specialDates = new Set() }) {
  const days = decorate(items || [], todayIso);
  const byUpside = (a, b) => b.upside - a.upside;
  // 相鄰日索引先建起來：鄰日在手率與鄰日中位預測都要看前一晚/後一晚，且相鄰指的是日曆上的前後，
  // 不是清單上的前後筆 (清單依影響金額排序)
  const byIso = new Map(days.map((d) => [d.iso, d]));
  const shift = (iso, n) => moment(iso, 'YYYY-MM-DD').add(n, 'day').format('YYYY-MM-DD');

  /** 鄰日在手率；查不到就是 null —— 清單頭尾沒有鄰日，「沒觀測到」不能填成 0。 */
  const occAt = (iso) => {
    const n = byIso.get(iso);
    return n && n.occNow != null ? n.occNow : null;
  };

  /** 鄰日中位預測；查不到或樣本不足就是 null —— 遠期在手率每天看起來都很弱，鄰日強弱要看預測。 */
  const midAt = (iso) => {
    const n = byIso.get(iso);
    return n && n.band ? n.band.mid : null;
  };

  const raise = days
    .filter((d) => isRaise(d.band)
      && d.lead >= RAISE_MIN_LEAD
      && d.unsold >= RAISE_MIN_ROOMS)
    .map((d) => {
      const prevMid = midAt(shift(d.iso, -1));
      const nextMid = midAt(shift(d.iso, 1));
      // 鄰日不到目標才提示最短住宿。null (沒有鄰日或樣本不足) 不算弱：不知道不等於弱
      const weakSides = [];
      if (prevMid != null && prevMid <= target) weakSides.push('前');
      if (nextMid != null && nextMid <= target) weakSides.push('後');
      return {
        ...d,
        // 同樣用保守界 (band.lo)：悲觀情境下還有多少住房率可以讓給漲價
        headroom: d.band.lo - RAISE_CEILING,
        // 團體撐盤：扣掉在手團體後就不夠格調漲，團體一取消住房率就掉下來。
        // 用 <= 與 isRaise 的嚴格大於對齊；otbGroup 為 null (舊快取) 時不知道，不標記
        groupRisk: d.otbGroup != null && d.cap > 0
          && d.band.lo - (d.otbGroup / d.cap) * 100 <= RAISE_CEILING,
        prevMid,
        nextMid,
        weakSides,
        nights: specialDates.has(d.iso) ? 3 : 2,
        // 漲 10% 後住房率掉到這裡為止 RevPAR 不變。超過 100% 的部分本來就賣不出去，以 100% 計
        raiseFloor10: Math.round((Math.min(d.band.lo, 100) / 1.1) * 10) / 10,
        // 滿房後預計擋掉的房租：用中位預測，這欄是排優先順序用的，門檻已由 band.lo 把關
        overflow: Math.round((Math.max(0, d.band.mid - 100) / 100) * (d.cap || 0) * (d.adr || 0)),
      };
    })
    .sort((a, b) => (b.overflow - a.overflow) || (b.headroom - a.headroom));

  const lower = days
    .filter((d) => isLower(d.band, target) && d.lead <= LOWER_MAX_LEAD)
    // gap 用保守界 (band.hi) 而非中位數，與 isLower 判調降的門檻 (band.hi < target) 對齊——
    // 調漲欄的 headroom 也是保守界 (band.lo)，兩欄同標準才公平；用 mid 會把調降的缺口灌水。
    .map((d) => ({
      ...d,
      gap: target - d.band.hi,
      breakEven10: breakEvenFill(d.occNow, 0.1),
      // 鄰日在手率：孤島型的低在手與整段都弱，處方不同
      prevOcc: occAt(shift(d.iso, -1)),
      nextOcc: occAt(shift(d.iso, 1)),
    }))
    .sort(byUpside);

  return { raise, lower };
}
